/**
 * 收入明细面板 - 展开显示指定收入类别的详细记录
 * 风格与 WorkTimePanel 等成本面板一致（sticky 表头 + 行列表）
 */
import React, { useState, useEffect } from 'react';
import { Spin, Empty } from 'antd';
import { ProjectContractService } from '../../../services/projectContractService';
import { additionService } from '../../../modules/progress/services';
import type { RevenueDetailType } from '../CostDetailTypes';
import type { IPaginationResponse, IAddition } from '../../../modules/progress/types';

interface RevenueDetailPanelProps {
  projectId: string;
  category: RevenueDetailType;
  isMobile?: boolean;
  active?: boolean;
  constructionType?: string;
}

interface ContractRecord {
  _id: string;
  contractNo?: string;
  partyA?: string;
  totalAmount?: number;
  status?: string;
  signedDate?: string;
}

interface AdditionRecord {
  _id: string;
  additionNo?: string;
  title?: string;
  category?: string;
  estimatedAmount?: number;
  actualAmount?: number;
  settlementAmount?: number;
  status?: string;
  createdAt?: string;
}

const catConfig: Record<RevenueDetailType, { label: string; color: string; desc: string }> = {
  contract: { label: '合同收入', color: '#9b8dc9', desc: '合同模块' },
  changeOrder: { label: '增项收入', color: '#5d8a7f', desc: '增项管理模块' },
  claim: { label: '签证收入', color: '#b99258', desc: '签证记录' },
  settlement: { label: '结算收入', color: '#d9a86b', desc: '结算记录' },
  other: { label: '其他收入', color: '#667986', desc: '其他来源' },
};

const fmt = (n: number): string => {
  if (Math.abs(n) >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
};

const RevenueDetailPanel: React.FC<RevenueDetailPanelProps> = ({
  projectId,
  category,
  isMobile = false,
  active = false,
  constructionType,
}) => {
  const [loading, setLoading] = useState(false);
  const [contractData, setContractData] = useState<ContractRecord[]>([]);
  const [additionData, setAdditionData] = useState<AdditionRecord[]>([]);
  const [loaded, setLoaded] = useState(false);

  const fetchData = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      if (category === 'contract') {
        const res = await ProjectContractService.getContracts({ projectId, constructionType, page: 1, limit: 500 });
        if (res.success && res.data) {
          setContractData(res.data as ContractRecord[]);
        }
      } else if (category === 'changeOrder') {
        const res = await additionService.getList({
          projectId,
          constructionType,
          excludedStatuses: 'cancelled,suspended',
          page: 1,
          limit: 500,
        });
        if (res.success && res.data) {
          const paginationData = res.data as IPaginationResponse<IAddition>;
          const list = paginationData.list || (res.data as any[]);
          setAdditionData(list as AdditionRecord[]);
        }
      }
    } catch (error) {
      console.error('获取收入明细失败:', error);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active) {
      setLoaded(false);
      setAdditionData([]);
      setContractData([]);
    }
  }, [active, projectId, category, constructionType]);

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId, category, constructionType]);

  if (!loaded && !active) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
        <Spin size="small" /> 加载中...
      </div>
    );
  }

  const config = catConfig[category];

  const renderContractRows = () => {
    if (!contractData.length) {
      return (
        <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 6, opacity: 0.35 }}>{'📄'}</div>
          <div>暂无合同记录</div>
          <div style={{ marginTop: 6 }}>
            <a
              href={`/project-contract-management?projectId=${projectId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12 }}
            >
              前往合同管理 →
            </a>
          </div>
        </div>
      );
    }
    return (
      <>
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '80px 1fr 100px 80px',
            gap: 8,
            padding: '6px 12px',
            background: 'rgba(0,0,0,0.03)',
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>编号</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>甲方</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>金额</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>状态</span>
          </div>
        )}
        {contractData.map((record, i) => (
          <div
            key={record._id}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr auto' : '80px 1fr 100px 80px',
              gap: 8,
              padding: '10px 12px',
              borderBottom: i < contractData.length - 1 ? '1px solid var(--border)' : 'none',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {record.contractNo || '-'}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {record.partyA || '-'}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace', textAlign: 'right' }}>
              {fmt(record.totalAmount || 0)}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>
              {record.status || '-'}
            </span>
          </div>
        ))}
      </>
    );
  };

  const renderAdditionRows = () => {
    if (!additionData.length) {
      return (
        <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 6, opacity: 0.35 }}>{'📝'}</div>
          <div>暂无增项记录</div>
        </div>
      );
    }
    return (
      <>
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '80px 1fr 70px 100px 80px',
            gap: 8,
            padding: '6px 12px',
            background: 'rgba(0,0,0,0.03)',
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>编号</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>增项名称</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>施工类型</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>金额</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>状态</span>
          </div>
        )}
        {additionData.map((record, i) => {
          const displayAmount = record.settlementAmount !== undefined ? record.settlementAmount
            : record.actualAmount !== undefined && record.actualAmount > 0 ? record.actualAmount
            : record.estimatedAmount !== undefined ? record.estimatedAmount : 0;
          const hasSettlement = record.settlementAmount !== undefined && record.settlementAmount > 0;
          const hasActual = record.actualAmount !== undefined && record.actualAmount > 0;
          const amountLabel = hasSettlement ? '结算' : hasActual ? '实际' : '预估';
          return (
            <div
              key={record._id}
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr auto' : '80px 1fr 70px 100px 80px',
                gap: 8,
                padding: '10px 12px',
                borderBottom: i < additionData.length - 1 ? '1px solid var(--border)' : 'none',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {record.additionNo || '-'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.title || '未命名增项'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {record.category || '-'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace', textAlign: 'right' }}>
                {fmt(displayAmount)}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {record.status || '-'}
              </span>
            </div>
          );
        })}
      </>
    );
  };

  const renderPlaceholder = () => (
    <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
      <div style={{ fontSize: 28, marginBottom: 6, opacity: 0.35 }}>
        {category === 'claim' ? '📋' : category === 'settlement' ? '✅' : '💰'}
      </div>
      <div>该类别暂不支持明细查看</div>
      <div style={{ fontSize: 11, marginTop: 4 }}>{config.desc}</div>
    </div>
  );

  const renderContent = () => {
    switch (category) {
      case 'contract': return renderContractRows();
      case 'changeOrder': return renderAdditionRows();
      default: return renderPlaceholder();
    }
  };

  return (
    <div>
      <style>{`
        .revenue-detail-row:hover { background: rgba(0,0,0,0.025) !important; }
      `}</style>

      {/* 面板标题栏 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderBottom: '1px solid var(--border)',
        background: `${config.color}10`,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: config.color, flexShrink: 0,
        }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: config.color }}>
          {config.label}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {config.desc}
        </span>
        {category === 'contract' && contractData.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
            {contractData.length} 份
          </span>
        )}
        {category === 'changeOrder' && additionData.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
            {additionData.length} 条
          </span>
        )}
      </div>

      {/* 内容区 */}
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default RevenueDetailPanel;
